import React from "react";
import { Box } from "@mui/material";
import CustomModal from "../CustomModal";
import { fontWeight } from "../../../utils/constants/UI";
import { GREY, BASE, ERROR } from "../../../utils/constants/colors";
import Typography from "../../Typography/Typography";
import Image from "../../Image/Image";
import { OOPS_IMAGE } from "../../../utils/constants/assets";
import Button from "../../Buttons/Button";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { PROFILE_PATH } from "../../../utils/constants/routes";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 32px 28px 32px",
    textAlign: "center",
    gap: "24px",
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(12px)",
    borderRadius: "16px",
    position: "relative",
    overflow: "hidden",
  },
  imageContainer: {
    width: "130px",
    height: "130px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    filter: "drop-shadow(0px 8px 16px rgba(0,0,0,0.1))",
  },
  image: {
    width: "100%",
    height: "auto",
    animation: "float 3s ease-in-out infinite",
    "@keyframes float": {
      "0%": { transform: "translateY(0px)" },
      "50%": { transform: "translateY(-10px)" },
      "100%": { transform: "translateY(0px)" },
    },
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 16px",
    borderRadius: "20px",
    backgroundColor: "rgba(231, 63, 63, 0.1)",
    border: `1px solid ${ERROR.W_200}`,
  },
  contentSection: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxWidth: "460px",
  },
  buttonSection: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "14px",
    marginTop: "8px",
  },
  buttonWrapper: {
    flex: 1,
    minWidth: 0,
  },
  cancelWrapper: {
    marginTop: "12px",
    cursor: "pointer",
    padding: "8px 16px",
    borderRadius: "6px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.05)",
    },
  },
};

const MissingCreditRecordModal = ({ open, close, message }) => {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.userDetails?.payload);
  const isAdmin = userDetails?.role === "admin-user";

  const handleClose = () => close(false);

  const handleBuyCredits = () => {
    navigate(`${PROFILE_PATH}?tab=Billing`);
    close(false);
  };

  const modalHeaderData = {
    heading: { label: "Credit Record Missing" },
  };

  const modalBodyData = (
    <Box sx={styles.container}>
      <Box sx={styles.imageContainer}>
        <Image src={OOPS_IMAGE} sx={styles.image} />
      </Box>

      <Box sx={styles.badge}>
        <Typography
          size="xs"
          color={ERROR.W_700}
          fontWeight={fontWeight.SEMIBOLD}
          label="Action Required"
        />
      </Box>

      <Box sx={styles.contentSection}>
        <Typography
          size="lg"
          color={GREY.PRIMARY}
          fontWeight={fontWeight.BOLD}
          label="Credit Record Missing"
        />
        <Typography
          size="sm"
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={
            message ||
            "We couldn't find your credit record. Please contact support at support@reachiq.io to resolve this issue."
          }
        />
      </Box>

      <Box sx={styles.buttonSection}>
        {isAdmin ? (
          <Box sx={styles.buttonWrapper}>
            <Button
              label="Buy More Credits"
              size="md40"
              variant="premium"
              onClick={handleBuyCredits}
            />
          </Box>
        ) : (
          <>
            <Box sx={styles.buttonWrapper}>
              <Button
                label="Contact Support"
                size="md40"
                variant="premium"
                onClick={() => {
                  window.open("mailto:support@reachiq.io", "_blank");
                  handleClose();
                }}
              />
            </Box>
            <Box sx={styles.buttonWrapper}>
              <Button
                label="Dismiss"
                size="md40"
                variant="ghost"
                onClick={handleClose}
              />
            </Box>
          </>
        )}
      </Box>

      <Box sx={styles.cancelWrapper} onClick={handleClose}>
        <Typography
          size="sm"
          color={GREY.TERTIARY}
          fontWeight={fontWeight.MEDIUM}
          label="Maybe Later"
        />
      </Box>
    </Box>
  );

  return (
    <CustomModal
      width="540px"
      close={handleClose}
      open={open}
      header={modalHeaderData}
      blurBackdrop={true}
    >
      {modalBodyData}
    </CustomModal>
  );
};

export default MissingCreditRecordModal;
